/**
 * LoanMath: pure loan arithmetic shared by the debt, property and asset flows. Rates are
 * annual percentages; the closed forms go through Double exactly as the Swift code does.
 */
import { powDouble } from '../core/pow.ts';
import {
  add, dec, div, eq, fromDouble, fromInt, gt, lt, lte, max, min, mul, sub, toDouble, ZERO, type Decimal,
} from '../core/decimal.ts';

export const FALLBACK_ANNUAL_PAYDOWN_PERCENT: Decimal = dec(3);

/** rate / 100 / 12: two truncating divisions, in that order. */
export const monthlyRate = (annualPercent: Decimal): Decimal => div(div(annualPercent, dec(100)), dec(12));

export interface MonthlyPaymentTerms {
  readonly principal: Decimal;
  readonly annualRatePercent: Decimal;
  readonly termMonths: number;
  readonly balloon?: Decimal;
  readonly interestOnly?: boolean;
}

/** Level payment amortising `principal` down to `balloon`; null without a principal or term. */
export function monthlyPayment(terms: MonthlyPaymentTerms): Decimal | null {
  const { principal, annualRatePercent, termMonths, balloon = ZERO, interestOnly = false } = terms;
  if (!gt(principal, ZERO) || termMonths <= 0) return null;
  if (interestOnly) return div(mul(principal, div(annualRatePercent, dec(100))), dec(12));
  if (eq(annualRatePercent, ZERO)) return div(sub(principal, balloon), fromInt(termMonths));

  const r = toDouble(monthlyRate(annualRatePercent));
  const p = toDouble(principal);
  const b = toDouble(balloon);
  const factor = powDouble(1 + r, termMonths);
  return fromDouble((p * r * factor - b * r) / (factor - 1));
}

/** P(1+r)^k − M((1+r)^k − 1)/r in Double, floored at 0; linear when r ≤ 0. */
export function balanceAfterPayments(principal: Decimal, payment: Decimal, rate: Decimal, paymentsMade: number): Decimal {
  if (paymentsMade <= 0) return principal;
  if (!gt(rate, ZERO)) return max(sub(principal, mul(payment, fromInt(paymentsMade))), ZERO);

  const r = toDouble(rate);
  const p = toDouble(principal);
  const m = toDouble(payment);
  const factor = powDouble(1 + r, paymentsMade);
  return max(fromDouble(p * factor - (m * (factor - 1)) / r), ZERO);
}

export const balanceAfterPaymentsAtAnnualRate = (
  principal: Decimal,
  payment: Decimal,
  annualRatePercent: Decimal,
  paymentsMade: number,
): Decimal => balanceAfterPayments(principal, payment, monthlyRate(annualRatePercent), paymentsMade);

export interface ScheduleTerms {
  readonly principal: Decimal;
  readonly annualRatePercent: Decimal;
  readonly termMonths: number;
  readonly paymentsMade: number;
  readonly interestOnly?: boolean;
}

/** Balance on the standard schedule: P((1+r)^n − (1+r)^k)/((1+r)^n − 1) in Double. */
export function scheduledBalance(terms: ScheduleTerms): Decimal {
  const { principal, annualRatePercent, termMonths, interestOnly = false } = terms;
  const elapsed = Math.max(0, terms.paymentsMade);
  if (elapsed >= termMonths) return ZERO;
  if (interestOnly) return principal;
  if (eq(annualRatePercent, ZERO)) {
    const monthlyPrincipal = div(principal, fromInt(termMonths));
    return max(sub(principal, mul(monthlyPrincipal, fromInt(elapsed))), ZERO);
  }

  const r = toDouble(monthlyRate(annualRatePercent));
  const toN = powDouble(1 + r, termMonths);
  const toK = powDouble(1 + r, elapsed);
  return max(fromDouble((toDouble(principal) * (toN - toK)) / (toN - 1)), ZERO);
}

/** Principal repaid after `monthsElapsed` at a straight-line annual percentage; not floored. */
export const linearPaydown = (
  originalLoan: Decimal,
  monthsElapsed: number,
  annualPercent: Decimal = FALLBACK_ANNUAL_PAYDOWN_PERCENT,
): Decimal => mul(div(mul(originalLoan, annualPercent), dec(100)), div(fromInt(monthsElapsed), dec(12)));

export interface EstimateTerms {
  readonly originalLoan: Decimal;
  readonly monthlyPayment: Decimal;
  readonly annualRatePercent: Decimal;
  readonly monthsElapsed: number;
}

/**
 * Onboarding mortgage estimate: simulate interest-then-payment monthly when the payment is
 * plausible (positive rate, payment under a tenth of the loan), else straight-line paydown.
 */
export function estimatedBalance(terms: EstimateTerms): Decimal {
  const { originalLoan, monthlyPayment: payment, annualRatePercent, monthsElapsed } = terms;
  const rate = monthlyRate(annualRatePercent);
  const plausible = gt(rate, ZERO) && gt(payment, ZERO) && lt(payment, div(originalLoan, dec(10)));
  if (!plausible) return max(sub(originalLoan, linearPaydown(originalLoan, monthsElapsed)), ZERO);

  let balance = originalLoan;
  for (let month = 0; month < Math.max(0, monthsElapsed); month++) {
    const interest = mul(balance, rate);
    balance = max(sub(balance, sub(payment, interest)), ZERO);
  }
  return balance;
}

export interface Payoff {
  readonly months: number;
  readonly totalPaid: Decimal;
  readonly interestPaid: Decimal;
  readonly isPaidOff: boolean;
}

/**
 * Month-by-month payoff: interest accrues, then the payment (capped at the balance) is made.
 * Null when there is nothing to pay or the first month's interest consumes the payment.
 */
export function payoff(balance: Decimal, annualRatePercent: Decimal, payment: Decimal, maxMonths = 600): Payoff | null {
  if (!gt(balance, ZERO) || !gt(payment, ZERO)) return null;
  const rate = monthlyRate(annualRatePercent);
  if (lte(payment, mul(balance, rate))) return null;

  let remaining = balance;
  let months = 0;
  let totalPaid: Decimal = ZERO;
  while (gt(remaining, ZERO) && months < maxMonths) {
    remaining = add(remaining, mul(remaining, rate));
    const paid = min(payment, remaining);
    remaining = sub(remaining, paid);
    totalPaid = add(totalPaid, paid);
    months += 1;
  }
  return {
    months,
    totalPaid,
    interestPaid: max(sub(totalPaid, balance), ZERO),
    isPaidOff: lte(remaining, ZERO),
  };
}
